import { roundHalfUpCents } from '../money';
import type { VisitFixedInput } from '../documents/visits';
/**
 * The service request route on a Service deal (piece 18; decisions 1912, 1990):
 * a third route beside Pipelined and Quick, the same `Deal` with `origin`
 * ServiceRequest, created Won, its own facts on the Deal's own columns (decision 2292).
 * 🔨 Nothing here stores the minimum: it is the billing minimum decision 0178
 * defines, the labour item's minimum quantity at its price, read from the tier.
 */

/** How a request came in, the `DealChannel` members in the order the form offers them. */
export const SERVICE_REQUEST_SOURCES = ['Phone', 'Email', 'InPerson', 'Text'] as const;
export type ServiceRequestSource = (typeof SERVICE_REQUEST_SOURCES)[number];

/** 🔨 A picker offers words and stores a member: `InPerson` is a column spelling, not language. */
export const SERVICE_REQUEST_SOURCE_LABELS: Readonly<Record<ServiceRequestSource, string>> = {
  Phone: 'Phone',
  Email: 'Email',
  InPerson: 'In person',
  Text: 'Text',
};

/** What a request's create carries beside the Deal's own name, account and type. */
export interface ServiceRequestDraft {
  dealType: string;
  /** The Deal's account, where the caller has one to check; the server's own check reads the company itself. */
  companyId?: string | null | undefined;
  /** The problem in the caller's words, which becomes the Deal's Need and the job's first line. */
  need: string | null | undefined;
  /** Who asked for the work: an Employment at the Deal's account, which becomes the Deal's Authority. */
  requestedByEmploymentId?: string | null | undefined;
  siteId: string | null | undefined;
  /** 🔒 How it came in, optional on the Service form (decision 2292; the owner, 2026-10-07). */
  source?: string | null | undefined;
  receivedAt?: string | Date | null | undefined;
  preferredWindowStart?: string | Date | null | undefined;
  preferredWindowEnd?: string | Date | null | undefined;
  ceilingCents?: number | null | undefined;
  /** 🔒 Saved with the Service form's Emergency switch on (decision 2294): how it came in is required, who asked is not. */
  emergency?: boolean | undefined;
}

const instant = (value: string | Date | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const at = value instanceof Date ? value : new Date(value);
  return Number.isNaN(at.getTime()) ? new Date(Number.NaN) : at;
};

/**
 * 🔒 Why a request cannot be opened, or null where it can (decision 1912). A
 * request is a Service deal; Authority and Need stay required; the site is the
 * one the job will stand at; it came in no later than now (decision 2410); the
 * window never ends before it starts and the ceiling is a figure at or above zero.
 */
export function serviceRequestProblem(draft: ServiceRequestDraft, now: Date = new Date()): string | null {
  if (draft.dealType !== 'Service') return 'a service request is a Service deal; choose Service as the type';
  if (draft.companyId !== undefined && !draft.companyId) return 'a service request names the account';
  if (!draft.need?.trim()) return 'a service request states the need, the problem in the caller’s words';
  // 🔒 An emergency asks no contact before it dispatches, as the emergency page asked none (decision 2294).
  if (!draft.requestedByEmploymentId && !draft.emergency) return 'a service request names who asked for the work, a contact at the account';
  if (!draft.siteId) return 'a service request names the site the work is at';
  if (draft.emergency && !draft.source) return 'an emergency says how it came in: phone, email, in person or text';
  if (draft.source && !(SERVICE_REQUEST_SOURCES as readonly string[]).includes(draft.source)) return 'how a service request came in is phone, email, in person or text';
  const received = instant(draft.receivedAt);
  if (received && Number.isNaN(received.getTime())) return 'when the request came in is a date and time';
  // 🔨 A request cannot come in after it is written down (decision 2410); a minute's grace covers a clock a little ahead.
  if (received && received.getTime() > now.getTime() + 60_000) return 'a request came in before it is saved, so its time is not later than now';
  const start = instant(draft.preferredWindowStart);
  const end = instant(draft.preferredWindowEnd);
  if ((start && Number.isNaN(start.getTime())) || (end && Number.isNaN(end.getTime()))) return 'the preferred window is a date and time';
  if (end && !start) return 'a preferred window that ends also starts';
  if (start && end && end.getTime() < start.getTime()) return 'the preferred window ends after it starts';
  if (draft.ceilingCents !== null && draft.ceilingCents !== undefined && (!Number.isInteger(draft.ceilingCents) || draft.ceilingCents < 0)) return 'the ceiling is a figure at or above zero';
  return null;
}

/** What the Service Request tile's Edit sends (decision 12600): each key absent is left as it stands; a null clears. */
export interface ServiceRequestEdit {
  /** The `tierKey` of the tier chosen, or null to let the tier derive from the preferred window again. */
  tierKey?: string | null;
  preferredWindowStart?: string | Date | null;
  preferredWindowEnd?: string | Date | null;
  ceilingCents?: number | null;
  /** The Deal's version the tile was read at; an Edit meeting another is refused as stale (decision 12600; T3825). */
  version?: number | null;
}

/**
 * 🔒 Why a request's Edit cannot be saved, or null where it can (decision 12600): the same window and ceiling
 * rules as the Service form, over the window as it will stand after the save.
 */
export function serviceRequestEditProblem(edit: ServiceRequestEdit): string | null {
  const start = instant(edit.preferredWindowStart);
  const end = instant(edit.preferredWindowEnd);
  if ((start && Number.isNaN(start.getTime())) || (end && Number.isNaN(end.getTime()))) return 'the preferred window is a date and time';
  if (end && !start) return 'a preferred window that ends also starts';
  if (start && end && end.getTime() < start.getTime()) return 'the preferred window ends after it starts';
  if (edit.ceilingCents !== null && edit.ceilingCents !== undefined && (!Number.isInteger(edit.ceilingCents) || edit.ceilingCents < 0)) return 'the ceiling is a figure at or above zero';
  return null;
}

/**
 * 🔒 The name a Deal from the Service form takes (piece 18, *New Deal by type*):
 * the form asks none, so the Deal is named for the account and the need's first
 * line, and the title's Edit renames it.
 */
export function serviceDealName(account: string, need: string): string {
  const first = need.trim().split('\n')[0]?.trim() ?? '';
  return (first ? `${account} · ${first}` : account).slice(0, 200);
}

/**
 * 🔒 The billing minimum in cents (decision 0178): the labour item's
 * `minimumSaleQuantity` at its sales price. An item with no minimum quantity, or
 * no price, bills no minimum, and the request's value reads zero until it is set.
 */
export function billingMinimumCents(labourItem: { minimumSaleQuantity: number | null; salesPrice: number | null } | null): number {
  if (!labourItem || labourItem.minimumSaleQuantity === null || labourItem.salesPrice === null) return 0;
  return Math.max(0, roundHalfUpCents(labourItem.minimumSaleQuantity * labourItem.salesPrice * 100));
}

/**
 * 🔒 The Deal's value on the request route (decision 1912): the billing minimum
 * until an invoice from its job issues, then the sum of the issued invoices from
 * its jobs. An issued invoice is any past Draft and short of Void.
 */
export function serviceRequestValueCents(minimumCents: number, issuedInvoiceCents: readonly number[]): { cents: number; source: 'requestMinimum' | 'invoiced' } {
  if (issuedInvoiceCents.length === 0) return { cents: minimumCents, source: 'requestMinimum' };
  return { cents: issuedInvoiceCents.reduce((sum, cents) => sum + cents, 0), source: 'invoiced' };
}

/** An invoice status that counts as issued for the request's value: past Draft and short of Void. */
export function invoiceCountsAsIssued(status: string): boolean {
  return status !== 'Draft' && status !== 'Void';
}

/** The route's three steps, in order (decision 1912). */
export const SERVICE_REQUEST_STEPS = ['request', 'workOrder', 'invoice'] as const;
export type ServiceRequestStep = (typeof SERVICE_REQUEST_STEPS)[number];

/**
 * 🔒 Which of the three steps are done: the Request always, the Work Order once a
 * job stands on the Deal that is not Void, the Invoice from the job once one has
 * issued. The step after the last done one is the current one.
 */
export function serviceRequestSteps(input: { workOrderStatuses: readonly string[]; invoiced: boolean }): { step: ServiceRequestStep; done: boolean; current: boolean }[] {
  const done: Record<ServiceRequestStep, boolean> = {
    request: true,
    workOrder: input.workOrderStatuses.some((status) => status !== 'Void'),
    invoice: input.invoiced,
  };
  const current = SERVICE_REQUEST_STEPS.find((step) => !done[step]) ?? null;
  return SERVICE_REQUEST_STEPS.map((step) => ({ step, done: done[step], current: step === current }));
}

/**
 * 🔒 What Raise Work Order's window sends (decision 2814; T2049): the visit's
 * Starts and Ends as wall clocks in the zone named, the workers going and their
 * lead. An empty Starts raises a Draft job with no visit, as an empty body does.
 */
export interface RaiseWorkOrderInput extends VisitFixedInput {
  startsAt?: string | null;
  endsAt?: string | null;
  timeZone?: string | null;
  workerEmploymentIds?: string[] | null;
  leadEmploymentId?: string | null;
}

/**
 * 🔒 Whether the raise books a visit, or why its window is refused (decision
 * 2814): a window books one; an end, a crew or a lead with no start is refused,
 * because a visit is booked by the day and time it starts.
 */
export function raiseWindowProblem(input: RaiseWorkOrderInput | null | undefined): { books: boolean; problem: string | null } {
  const startsAt = input?.startsAt?.trim() ?? '';
  if (startsAt) return { books: true, problem: input?.timeZone?.trim() ? null : 'the visit names the time zone its start is read in' };
  const crew = input?.workerEmploymentIds;
  const named = Boolean(input?.endsAt?.trim()) || (Array.isArray(crew) ? crew : []).some((id) => typeof id === 'string' && id.length > 0) || Boolean(input?.leadEmploymentId);
  return { books: false, problem: named ? 'a visit is booked with the day and time it starts; name the start, or clear the end and the crew to raise the job unbooked' : null };
}

/**
 * 🔒 Raise Work Order stands on a request with no live job yet (decision 1912):
 * a second job is raised from the Work Order's own Follow-Up, never from here.
 */
export function raiseWorkOrderProblem(input: { origin: string; stage: string; workOrderStatuses: readonly string[] }): string | null {
  if (input.origin !== 'ServiceRequest') return 'Raise Work Order stands on a service request';
  if (input.stage === 'Lost') return 'a lost request raises no job';
  if (input.workOrderStatuses.some((status) => status !== 'Void')) return 'this request already has its job; a further visit is booked on it, or a follow-up raised from it';
  return null;
}
